import { DEFAULT_PAGE_SIZE, Dataset, DtFilter, DtSort } from '@/components/data-table/helpers';
import {
  PublicServiceMeta,
  PublicServiceModel,
} from '../models/public-service/public-service.model';
import { JsonObject, UiOption, downloadCsv, downloadFile, getNiceDate } from '../helpers';
import { cloneDeep, difference, escapeRegExp, flatten, orderBy, uniq } from 'lodash';
import { ListResponse } from '.';
import { formatISO } from 'date-fns';
import { get } from './sp-api.service';
import { UserEventModel } from '../models/user-event/user-event.model';
import F5Logomark from '../../assets/f5-logo-rgb.svg';
import { PolicyMeta } from '../models/policy-model-v1';
import { getPoliciesByServiceIdV1 } from './policy-v1.service';

export type ServiceUsage = { policies: PolicyMeta[]; inUse: boolean };

const SAFE_LIMIT = 1000;

export type PublicCatalogDownloadFormat =
  | 'zscaler'
  | 'netskope'
  | 'csv'
  | 'cisco-umbrella'
  | 'cloudflare'
  | 'palo-domain'
  | 'palo-url'
  | 'generic-sase';

export const PublicCatalogDownloadOpts: UiOption[] = [
  { label: 'Cloudflare Config', value: 'cloudflare' },
  { label: 'Generic Domain List', value: 'generic-sase' },
  { label: 'Netskope Config', value: 'netskope' },
  { label: 'Palo Alto Domain EDL', value: 'palo-domain' },
  { label: 'Palo Alto URL EDL', value: 'palo-url' },
  { label: 'Zscaler Config', value: 'zscaler' },
  // { label: 'Cisco Umbrella Config (Events API)', value: 'cisco-umbrella' },
  // { label: 'CSV', value: 'csv' },
];

export const getPublicServiceAppData = async (): Promise<PublicServiceModel[]> => {
  const fields = ['name', 'image', 'description', 'risk', 'support', 'type', 'signals', 'tags'];
  return getAllPublicServices(fields);
};

export const getAllPublicServices = async (fields?: string[]): Promise<PublicServiceModel[]> => {
  const { data } = (await get('/models/public-services', {
    page: 1,
    pageSize: SAFE_LIMIT,
    fields,
  })) as ListResponse;
  return (data || []).map((row) => new PublicServiceModel(row));
};

export const getPublicServices = async (
  page = 0,
  pageSize: number = DEFAULT_PAGE_SIZE,
  dtSort?: DtSort,
  dtFilter?: DtFilter,
  publicServices?: PublicServiceModel[]
): Promise<Dataset<PublicServiceModel>> => {
  let models = publicServices ? cloneDeep(publicServices) : await getAllPublicServices();

  if (dtFilter?.keyword || dtFilter?.values?.length) {
    const { keyword, values = [] } = dtFilter;
    const safeKeyword = keyword ? String(keyword).toLowerCase() : '';

    models = models.filter(({ name, companyName, website, signals, risk, interceptFeatures }) => {
      // keyword match on name, companyName, website
      if (
        safeKeyword &&
        ![name, companyName, website].some((val) => String(val).toLowerCase().includes(safeKeyword))
      ) {
        return false;
      }

      // create and AND filter by multiple values
      for (const { columnName, value } of values) {
        switch (columnName) {
          case 'interceptFeatures':
            {
              const features = value as string[];

              if (!interceptFeatures.some(({ value }) => features.includes(value))) {
                return false;
              }
            }
            break;
          case 'riskSignals': {
            const signalKeys = value as string[];

            if (signalKeys.includes('trainOnUserData') && !signals?.trainOnUserData?.value) {
              return false;
            }

            if (signalKeys.includes('noModeration') && !signals?.noModeration?.value) {
              return false;
            }
            break;
          }
          case 'riskLevel': {
            const levels = value as string[];
            if (!levels.includes(risk?.base?.level)) {
              return false;
            }
            break;
          }
        }
      }

      return true;
    });
  }

  type SortParam = string | ((document: PublicServiceModel) => number | string);
  const { columnName, direction } = dtSort || {
    columnName: 'interceptFeatures',
    direction: 'desc',
  };

  let fieldParam: SortParam[] = [];
  const directionParam = [direction];

  switch (columnName) {
    case 'name':
      fieldParam = ['name'];
      break;
    default:
    case 'interceptFeatures':
      fieldParam = ['interceptScore'];
      break;
    case 'risk':
      fieldParam = ['risk.base.score'];
      break;
    case 'company':
      fieldParam = ['companyName'];
      break;
  }

  const sorted = orderBy(models, fieldParam, directionParam);
  const skip = page * pageSize;
  const total = sorted.length;
  const rows: PublicServiceModel[] = sorted.slice(skip, skip + pageSize);

  return {
    page,
    pageSize,
    rows,
    total,
  };
};

export const getPublicServiceById = async (modelId: string): Promise<PublicServiceModel | null> => {
  const response = await get(`/models/public-services/${modelId}`);
  return response ? new PublicServiceModel(response as JsonObject) : null;
};

export const getServiceUsage = async (serviceId: string): Promise<ServiceUsage> => {
  const policyModels = await getPoliciesByServiceIdV1(serviceId);
  const policies = policyModels.map(({ meta }) => meta);

  return { policies, inUse: !!policies.length };
};

export const downloadCatalog = async (
  format: PublicCatalogDownloadFormat,
  orgId?: string
): Promise<void> => {
  const publicServices = await getAllPublicServices();

  let hosts = flatten(
    publicServices
      .filter(({ support }) => !support.deprecated?.value)
      .map(({ hosts, unauthenticatedHosts }) => [...hosts, ...unauthenticatedHosts])
  ).map((host) => String(host).toLowerCase());

  // unique + remove surepath hosts
  hosts = uniq(hosts).filter((host) => !host.includes('surepath'));

  // alphabetize
  hosts.sort();

  const { date } = getNiceDate(new Date());
  const filename = `f5-workforce-ai-security-public-services-${format}-${date}`;

  switch (format) {
    case 'zscaler':
    case 'netskope':
    case 'generic-sase':
      {
        let fileContent: string[] = [];

        /*
         * We need to differentiate between zscaler and netskope/generic-sase here because wildcards are represented different in
         * either system. ZScaler wildcards look like this: `.openai.com` whereas Netskope uses a star like this `*.openai.com`
         *
         * https://help.zscaler.com/zia/url-format-guidelines
         */
        if (format === 'zscaler') {
          fileContent = hosts.map((host) => host.replace(/^\*{1}/, ''));
        } else {
          fileContent = hosts.map((host) => host.replace(/^\.{1}/, '*.'));
        }

        const fileBlob = new Blob([fileContent.join('\r\n')], { type: 'text/plain' });
        downloadFile(fileBlob, `${filename}.txt`);
      }
      break;

    case 'cisco-umbrella':
      {
        const eventTime = formatISO(new Date());

        const dstHosts = hosts.map((host) => {
          const domain = host.replace(/^\*?\./, '');

          return {
            dstDomain: domain,
            protocolVersion: '1.0a',
            providerName: 'F5 Workforce AI Security',
            deviceId: orgId || '',
            deviceVersion: '1.0',
            eventTime,
            alertTime: eventTime,
            dstUrl: `https://${domain}`,
            disableDstSafeguards: true,
          };
        });

        const fileBlob = new Blob([JSON.stringify(dstHosts, null, 2)], { type: 'text/plain' });
        downloadFile(fileBlob, `${filename}.json`);
      }
      break;

    case 'csv':
      {
        const headers = ['Name', 'Description', 'Support', 'Type', 'Hosts'];
        const csvData = publicServices.map(({ name, description, support, type, hosts }) => [
          name,
          description,
          support.level,
          type,
          hosts.join(','),
        ]);

        downloadCsv([headers, ...csvData], `${filename}.csv`);
      }
      break;

    case 'cloudflare':
      {
        const wildcardHosts = hosts.filter((host) => host.startsWith('*.'));
        const nonWildcardHosts = difference(hosts, wildcardHosts);

        const wildCardExp = wildcardHosts
          .map((host) => escapeRegExp(host.replace(/^\*{1}\.{1}/, '')))
          .join('|');

        const nonWildCardExp = nonWildcardHosts.map(escapeRegExp).join('|');

        const fileContent = `((^|\\.)(${wildCardExp})|^(${nonWildCardExp}))$`;
        const fileBlob = new Blob([fileContent], { type: 'text/plain' });
        downloadFile(fileBlob, `${filename}.txt`);
      }
      break;

    case 'palo-domain':
    case 'palo-url':
      {
        const isUrl = format === 'palo-url';
        const fileContent: string[] = [];
        const prefixChar = isUrl ? '' : '^';

        hosts.forEach((host) => {
          const isWildcard = host.startsWith('*.');
          if (isWildcard) {
            fileContent.push(host);
          }

          fileContent.push(`${prefixChar}${host.replace(/^\*{1}\.{1}/, '')}`);
        });

        const fileBlob = new Blob([fileContent.join('\r\n')], { type: 'text/plain' });
        downloadFile(fileBlob, `${filename}.txt`);
      }
      break;
  }
};

export const getUserEventPublicService = (
  event: UserEventModel,
  publicServices: PublicServiceMeta[]
): PublicServiceMeta | null => {
  const { serviceId, isF5WAIS, productServiceName: surePathServiceName } = event;

  if (serviceId) {
    const service = publicServices.find(({ id }) => id === serviceId);
    if (service) {
      return service;
    }
  }

  if (isF5WAIS) {
    return new PublicServiceModel({
      id: '',
      type: '',
      name: surePathServiceName,
      image: F5Logomark,
    });
  }

  return null;
};
